import React from "react";

/**
 * Slab-styled form controls.
 *
 * Inputs use a chunky Duolingo-style slab treatment:
 * - thick rounded border
 * - subtle bottom lip shadow
 * - animated focus ring
 * - invalid state palette
 * - optional suffix icon inside Input
 */

export type InputAccent = "blue" | "teal";

const ACCENT_FOCUS: Record<InputAccent, string> = {
  blue: "focus-within:border-[#1CB0F6] focus-within:shadow-[0_3px_0_#7CC4F0] focus-within:ring-[#1CB0F6]/25",
  teal: "focus-within:border-[#14B8A6] focus-within:shadow-[0_3px_0_#5EEAD4] focus-within:ring-[#14B8A6]/25",
};

const CONTROL_FOCUS: Record<InputAccent, string> = {
  blue: "focus:border-[#1CB0F6] focus:shadow-[0_3px_0_#7CC4F0] focus:ring-[#1CB0F6]/25",
  teal: "focus:border-[#14B8A6] focus:shadow-[0_3px_0_#5EEAD4] focus:ring-[#14B8A6]/25",
};

const wrapperBaseClass =
  "group flex min-h-[50px] w-full items-center rounded-2xl border-2 border-[#E5E5E5] bg-white shadow-[0_3px_0_#E5E5E5] ring-0 transition-all duration-150 focus-within:ring-4 disabled:cursor-not-allowed";

const inputInnerClass =
  "min-h-[46px] w-full min-w-0 flex-1 bg-transparent px-4 py-2.5 text-[15px] font-bold text-[#3C3C3C] outline-none placeholder:font-semibold placeholder:text-[#AFAFAF] disabled:cursor-not-allowed disabled:opacity-60";

const suffixIconClass =
  "flex h-9 mr-4 min-w-4 shrink-0 items-center justify-center rounded-xl text-[#777777] transition-colors group-focus-within:text-[#3C3C3C]";

const controlBaseClass =
  "min-h-[50px] w-full rounded-2xl border-2 border-[#E5E5E5] bg-white px-4 py-2.5 text-[15px] font-bold text-[#3C3C3C] shadow-[0_3px_0_#E5E5E5] ring-0 transition-all duration-150 placeholder:font-semibold placeholder:text-[#AFAFAF] focus:outline-none focus:ring-4 disabled:cursor-not-allowed disabled:opacity-60";

const invalidWrapperClass =
  "border-[#FF4B4B] shadow-[0_3px_0_#FCA5A5] focus-within:border-[#FF4B4B] focus-within:shadow-[0_3px_0_#FCA5A5] focus-within:ring-[#FF4B4B]/25";

const invalidControlClass =
  "border-[#FF4B4B] shadow-[0_3px_0_#FCA5A5] focus:border-[#FF4B4B] focus:shadow-[0_3px_0_#FCA5A5] focus:ring-[#FF4B4B]/25";

interface BaseProps {
  /** When true, swaps the palette to red for inline validation feedback. */
  invalid?: boolean;

  /** Focus accent. Default "blue" — set "teal" for Happening Now. */
  accent?: InputAccent;
}

function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(" ");
}

function controlClasses({
  invalid,
  accent = "blue",
  extra,
  className,
}: {
  invalid?: boolean;
  accent?: InputAccent;
  extra?: string;
  className?: string;
}): string {
  return cx(
    controlBaseClass,
    invalid ? invalidControlClass : CONTROL_FOCUS[accent],
    extra,
    className,
  );
}

function wrapperClasses({
  invalid,
  accent = "blue",
  disabled,
  className,
}: {
  invalid?: boolean;
  accent?: InputAccent;
  disabled?: boolean;
  className?: string;
}): string {
  return cx(
    wrapperBaseClass,
    invalid ? invalidWrapperClass : ACCENT_FOCUS[accent],
    disabled && "pointer-events-none opacity-60",
    className,
  );
}

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> &
  BaseProps & {
    /**
     * Optional icon or action rendered inside the input on the right.
     *
     * Examples:
     * suffixIcon={<Search className="h-4 w-4" />}
     * suffixIcon={<button type="button">Clear</button>}
     */
    suffixIcon?: React.ReactNode;

    /**
     * Optional className for the suffix wrapper.
     */
    suffixIconClassName?: string;
  };

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      invalid,
      accent = "blue",
      type = "text",
      suffixIcon,
      suffixIconClassName,
      disabled,
      ...rest
    },
    ref,
  ) => {
    if (!suffixIcon) {
      return (
        <input
          ref={ref}
          type={type}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          className={controlClasses({
            invalid,
            accent,
            className,
          })}
          {...rest}
        />
      );
    }

    return (
      <div
        className={wrapperClasses({
          invalid,
          accent,
          disabled,
          className,
        })}
      >
        <input
          ref={ref}
          type={type}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          className={inputInnerClass}
          {...rest}
        />

        <span
          className={cx(suffixIconClass, suffixIconClassName)}
          aria-hidden={!React.isValidElement(suffixIcon)}
        >
          {suffixIcon}
        </span>
      </div>
    );
  },
);

Input.displayName = "Input";

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> &
  BaseProps;

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, invalid, accent = "blue", rows = 3, ...rest }, ref) => (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={controlClasses({
        invalid,
        accent,
        extra: "min-h-[104px] resize-y py-3 leading-relaxed",
        className,
      })}
      {...rest}
    />
  ),
);

Textarea.displayName = "Textarea";

export type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement> &
  BaseProps;

/** Slab-styled `<select>` — same border treatment as the text inputs. */
export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, invalid, accent = "blue", children, style, ...rest }, ref) => (
    <select
      ref={ref}
      aria-invalid={invalid || undefined}
      className={controlClasses({
        invalid,
        accent,
        extra:
          "cursor-pointer appearance-none bg-[length:1rem] bg-[right_1rem_center] bg-no-repeat pr-10 font-black",
        className,
      })}
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23777777' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
        ...style,
      }}
      {...rest}
    >
      {children}
    </select>
  ),
);

Select.displayName = "Select";

export default Input;
